import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Diamond, type LucideIcon } from 'lucide-react';

export interface PricingTier {
  name: string;
  forWho: string;
  price: string;
  period: string;
  priceNote?: string;
  features: string[];
  footnote?: string;
  highlighted?: boolean;
  badgeText?: string;
  Icon?: LucideIcon;
  ctaText: string;
  ctaHref: string;
}

const cardVariants: Variants = {
  initial: { scale: 1, y: 0 },
  hover: {
    scale: 1.03,
    y: -5,
    transition: { type: 'spring', stiffness: 300, damping: 20 },
  },
};

const imageVariants: Variants = {
  initial: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.1,
    rotate: -5,
    transition: { type: 'spring', stiffness: 300, damping: 20 },
  },
};

/**
 * PricingTierCard - karta pakietu w stylu shadcn (hover lift + scale, diamentowe bullety,
 * liniowa ikona lucide w rogu). Wariant highlighted: akcentowa ramka, badge i lekki gradient.
 * Kolory wylacznie z tokenow motywu daien. Hover wylaczony przy prefers-reduced-motion (a11y).
 */
export function PricingTierCard({
  name,
  forWho,
  price,
  period,
  priceNote,
  features,
  footnote,
  highlighted = false,
  badgeText,
  Icon,
  ctaText,
  ctaHref,
}: PricingTier) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      whileHover={reduceMotion ? undefined : 'hover'}
      className={`relative flex h-full flex-col justify-between rounded-2xl p-6 md:p-8 shadow-sm transition-shadow duration-300 hover:shadow-2xl ${
        highlighted
          ? 'border-2 border-accent-from bg-gradient-to-b from-accent-from/10 via-surface to-surface'
          : 'border border-stroke bg-surface'
      }`}
    >
      {badgeText && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent-to px-4 py-1 text-xs font-semibold uppercase tracking-wider text-bg">
          {badgeText}
        </span>
      )}

      <div className="flex flex-col space-y-4">
        {/* Naglowek + ikona */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold text-text-primary">{name}</h3>
            <div className="mt-2">
              <span className="text-4xl font-bold tracking-tight text-text-primary">{price}</span>
              <p className="text-sm text-muted">{period}</p>
            </div>
            {priceNote && <p className="mt-2 text-sm font-medium text-accent-to">{priceNote}</p>}
          </div>
          {Icon && (
            <motion.div
              variants={imageVariants}
              aria-hidden="true"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-from/10 text-accent-to"
            >
              <Icon className="h-6 w-6" strokeWidth={1.75} />
            </motion.div>
          )}
        </div>

        {/* Opis */}
        <p className="text-sm leading-relaxed text-muted">{forWho}</p>

        {/* Lista cech z diamentami */}
        <ul className="space-y-2 pt-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-sm text-text-primary">
              <Diamond aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-accent-from" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {footnote && <p className="pt-1 text-xs italic text-muted">{footnote}</p>}
      </div>

      {/* CTA */}
      <a
        href={ctaHref}
        aria-label={`${ctaText} - pakiet ${name}`}
        className="btn-animated mt-8 inline-flex w-full items-center justify-center rounded-full bg-text-primary px-6 py-3 text-sm font-medium text-bg transition-colors hover:bg-text-primary/90"
      >
        {ctaText}
      </a>
    </motion.div>
  );
}
